import type { RouteObject } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { canAccessSystemSettings, canAccessUserSearch } from "@/components/PortfolioSidebar/permissions";
import { redirectFromLoader } from "@/components/Routing/redirectFromLoader";
import { RequireAuth } from "@/components/Routing/RequireAuth";
import { RequireFeatureFlag } from "@/components/Routing/RequireFeatureFlag";
import { RequirePortfolioAuth } from "@/components/Routing/RequirePortfolioAuth";
import { shouldRevalidateOnPathChange } from "@/providers/dashboardStartup";

import { PortfolioAnalyticsScope } from "./components/PortfolioAnalyticsScope";
import { getStoredPortfolioOverviewPeriodUrl } from "./pages/overview/portfolioOverviewPeriodStorage";
import { getStoredPortfolioTicketFiltersUrl } from "./pages/portfolio-ticket-list/portfolioTicketFilterStorage";
import { portfolioRoutes } from "./routes";

export const portfolioRoute = [
  {
    path: portfolioRoutes.overview,
    lazy: async () => {
      const { PortfolioOverviewLegacyPage } = await import("./pages/overview-legacy/PortfolioOverviewLegacyPage");

      return { element: <PortfolioOverviewLegacyPage /> };
    },
  },
  {
    path: portfolioRoutes.newProject,
    lazy: async () => {
      const { CreateProjectPage } = await import("@/modules/project/pages/create-or-edit/CreateProjectPage");

      return {
        element: (
          <RequireAuth permission={canAccessSystemSettings}>
            <CreateProjectPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: portfolioRoutes.userLookup,
    lazy: async () => {
      const { UsersLookupPage } = await import("./pages/users-lookup/UsersLookupPage");

      return {
        element: (
          <RequireAuth permission={canAccessUserSearch}>
            <UsersLookupPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: portfolioRoutes.addAdmin,
    lazy: async () => {
      const { AddAdminPage } = await import("./pages/add-admin/AddAdminPage");

      return {
        element: (
          <RequirePortfolioAuth permission={(x) => x.canInviteAdmins}>
            <AddAdminPage />
          </RequirePortfolioAuth>
        ),
      };
    },
  },
  {
    path: portfolioRoutes.bulkUploadAddresses,
    lazy: async () => {
      const { BulkUploadAddressesPage } = await import("./pages/bulk-upload-addresses/BulkUploadAddressesPage");

      return {
        element: (
          <RequireFeatureFlag flag="isPortfolioBulkUploadAddressAvailable">
            <RequirePortfolioAuth permission={(x) => x.canMassCreateAddresses}>
              <BulkUploadAddressesPage />
            </RequirePortfolioAuth>
          </RequireFeatureFlag>
        ),
      };
    },
  },
  {
    path: portfolioRoutes.createMassMessage,
    lazy: async () => {
      const { CreateMassPostPage } = await import("./pages/create-mass-post/CreateMassPostPage");

      return { element: <CreateMassPostPage /> };
    },
  },
  {
    path: portfolioRoutes.createMassSurvey,
    lazy: async () => {
      const { CreateMassSurveyPage } = await import("./pages/create-mass-survey/CreateMassSurveyPage");

      return { element: <CreateMassSurveyPage /> };
    },
  },
  {
    path: portfolioRoutes.tickets,
    // Restore the filters stored earlier in this tab before the page renders, so it only queries the filtered list.
    loader: ({ request }) => {
      const storedFiltersUrl = getStoredPortfolioTicketFiltersUrl(new URL(request.url));

      return storedFiltersUrl ? redirectFromLoader(request, storedFiltersUrl) : null;
    },
    shouldRevalidate: shouldRevalidateOnPathChange,
    lazy: async () => {
      const { PortfolioTicketListPage } = await import("./pages/portfolio-ticket-list/PortfolioTicketListPage");

      return { element: <PortfolioTicketListPage /> };
    },
  },
  {
    path: portfolioRoutes.ticketSettings,
    lazy: async () => {
      const { TicketSettingsPage } = await import("./pages/ticket-settings/TicketSettingsPage");

      return { element: <TicketSettingsPage /> };
    },
  },
  {
    path: portfolioRoutes.chats,
    lazy: async () => {
      const { PortfolioChatListPage } = await import("./pages/chat-list/PortfolioChatListPage");

      return { element: <PortfolioChatListPage /> };
    },
  },
  {
    path: portfolioRoutes.integrations,
    lazy: async () => {
      const { PortfolioIntegrationsPage } = await import("./pages/portfolio-integrations/PortfolioIntegrationsPage");

      return { element: <PortfolioIntegrationsPage /> };
    },
  },
  {
    path: portfolioRoutes.manageAiIntegration,
    lazy: async () => {
      const { AiPortfolioIntegrationPage } = await import("./pages/portfolio-integrations/AiPortfolioIntegrationPage");

      return { element: <AiPortfolioIntegrationPage /> };
    },
  },
  {
    path: portfolioRoutes.manageRemsIntegration,
    lazy: async () => {
      const { RemsPortfolioIntegrationPage } =
        await import("./pages/portfolio-integrations/RemsPortfolioIntegrationPage");

      return {
        element: (
          <RequireAuth permission={canAccessSystemSettings}>
            <RemsPortfolioIntegrationPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: portfolioRoutes.manageILoqIntegration,
    lazy: async () => {
      const { ILoqPortfolioIntegrationPage } =
        await import("./pages/portfolio-integrations/ILoqPortfolioIntegrationPage");

      return {
        element: (
          <RequireAuth permission={canAccessSystemSettings}>
            <ILoqPortfolioIntegrationPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: portfolioRoutes.integrationDetails,
    lazy: async () => {
      const { AvailableIntegrationDetailPage } =
        await import("./pages/available-integration-detail/AvailableIntegrationDetailPage");

      return { element: <AvailableIntegrationDetailPage /> };
    },
  },
  {
    path: portfolioRoutes.analyticsOverview,
    // Restore the reporting period stored earlier in this tab before the page renders, so it is only queried once.
    loader: ({ request }) => {
      const storedPeriodUrl = getStoredPortfolioOverviewPeriodUrl(new URL(request.url));

      return storedPeriodUrl ? redirectFromLoader(request, storedPeriodUrl) : null;
    },
    shouldRevalidate: shouldRevalidateOnPathChange,
    lazy: async () => {
      const { PortfolioOverviewPage } = await import("./pages/overview/PortfolioOverviewPage");

      return { element: <PortfolioOverviewPage /> };
    },
  },
  {
    path: portfolioRoutes.analyticsCommunityFeed,
    lazy: async () => {
      const { CommunityFeedAnalyticsPage } =
        await import("@/modules/analytics/pages/community-feed/CommunityFeedAnalyticsPage");

      return {
        element: (
          <PortfolioAnalyticsScope key="community-feed">
            <CommunityFeedAnalyticsPage />
          </PortfolioAnalyticsScope>
        ),
      };
    },
  },
  {
    path: portfolioRoutes.analyticsSentiment,
    lazy: async () => {
      const { CommunityFeedSentimentAnalyticsPage } =
        await import("@/modules/analytics/pages/community-feed-sentiment/CommunityFeedSentimentAnalyticsPage");

      return {
        element: (
          <PortfolioAnalyticsScope key="sentiment">
            <CommunityFeedSentimentAnalyticsPage />
          </PortfolioAnalyticsScope>
        ),
      };
    },
  },
  {
    path: portfolioRoutes.analyticsEvents,
    lazy: async () => {
      const { EventAnalyticsPage } = await import("@/modules/analytics/pages/events/EventAnalyticsPage");

      return {
        element: (
          <PortfolioAnalyticsScope key="events">
            <EventAnalyticsPage />
          </PortfolioAnalyticsScope>
        ),
      };
    },
  },
  {
    path: portfolioRoutes.analyticsTickets,
    lazy: async () => {
      const { TicketAnalyticsPage } = await import("@/modules/analytics/pages/tickets/TicketAnalyticsPage");

      return {
        element: (
          <PortfolioAnalyticsScope key="tickets">
            <TicketAnalyticsPage />
          </PortfolioAnalyticsScope>
        ),
      };
    },
  },
  {
    path: portfolioRoutes.analyticsBookings,
    lazy: async () => {
      const { BookingAnalyticsPage } = await import("@/modules/analytics/pages/bookings/BookingAnalyticsPage");

      return {
        element: (
          <PortfolioAnalyticsScope key="bookings">
            <BookingAnalyticsPage />
          </PortfolioAnalyticsScope>
        ),
      };
    },
  },
  {
    path: portfolioRoutes.analyticsExplore,
    lazy: async () => {
      const { AnalyticsExplorePage } = await import("@/modules/analytics/pages/explore/AnalyticsExplorePage");

      return { element: <AnalyticsExplorePage scope="portfolio" /> };
    },
  },
  {
    path: portfolioRoutes.analyticsExploreEvents,
    lazy: async () => {
      const { EventAnalyticsPage } = await import("@/modules/analytics/pages/events/EventAnalyticsPage");

      return {
        element: (
          <PortfolioAnalyticsScope key="explore-events">
            <EventAnalyticsPage mode="all" />
          </PortfolioAnalyticsScope>
        ),
      };
    },
  },
  {
    path: portfolioRoutes.analyticsExploreBookings,
    lazy: async () => {
      const { BookingAnalyticsPage } = await import("@/modules/analytics/pages/bookings/BookingAnalyticsPage");

      return {
        element: (
          <PortfolioAnalyticsScope key="explore-bookings">
            <BookingAnalyticsPage mode="all" />
          </PortfolioAnalyticsScope>
        ),
      };
    },
  },
  {
    path: portfolioRoutes.analyticsExploreCommunityFeed,
    lazy: async () => {
      const { CommunityFeedAnalyticsPage } =
        await import("@/modules/analytics/pages/community-feed/CommunityFeedAnalyticsPage");

      return {
        element: (
          <PortfolioAnalyticsScope key="explore-community-feed">
            <CommunityFeedAnalyticsPage mode="all" />
          </PortfolioAnalyticsScope>
        ),
      };
    },
  },
  {
    path: portfolioRoutes.analyticsExploreSentiment,
    lazy: async () => {
      const { CommunityFeedSentimentAnalyticsPage } =
        await import("@/modules/analytics/pages/community-feed-sentiment/CommunityFeedSentimentAnalyticsPage");

      return {
        element: (
          <PortfolioAnalyticsScope key="explore-sentiment">
            <CommunityFeedSentimentAnalyticsPage mode="all" />
          </PortfolioAnalyticsScope>
        ),
      };
    },
  },
  {
    path: portfolioRoutes.analyticsExploreTickets,
    lazy: async () => {
      const { TicketAnalyticsPage } = await import("@/modules/analytics/pages/tickets/TicketAnalyticsPage");

      return {
        element: (
          <PortfolioAnalyticsScope key="explore-tickets">
            <TicketAnalyticsPage mode="all" />
          </PortfolioAnalyticsScope>
        ),
      };
    },
  },
  { path: "*", element: <ErrorPage status={404} /> },
] satisfies RouteObject[];
